import { useState, useMemo, useRef, useCallback, type MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
  Select,
  Pill,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconFilter,
  IconRefresh,
  IconSend,
  IconRotate,
} from "@tabler/icons-react";
import {
  useUsersAdmin,
  useCreateUser,
  useUpdateUser,
  useToggleUserActive,
  useSetDefaultQaReviewer,
  useClearDefaultQaReviewer,
  useSendInvitation,
} from "../../../hooks/admin/useUsers";
import { useRolesAdmin } from "../../../hooks/admin/useRoles";
import UserForm, {
  UserFormValues,
} from "../../../components/admin/users/UserForm";
import { User, usersService } from "../../../services/admin/users.service";
import { useAuthStore } from "../../../store/auth.store";

const ITEMS_PER_PAGE = 20;

const showUserLoadError = () => {
  notifications.show({
    title: "Could not open user",
    message: "The latest user details could not be loaded. Please try again.",
    color: "red",
  });
};

function getUserStatusBadge(user: User): { color: string; label: string } {
  if (!user.is_active) return { color: "red", label: "Inactive" };
  const inv = user.invitationStatus || "not_invited";
  if (inv === "activated") return { color: "green", label: "Active" };
  if (inv === "invited") return { color: "yellow", label: "Pending" };
  return { color: "gray", label: "Not Invited" };
}

interface UserFilters {
  roleId?: string | null;
  status?: "all" | "active" | "inactive";
  defaultQaReviewer?: "all" | "yes" | "no";
}

export default function UsersAdminPage() {
  const queryClient = useQueryClient();
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    resendConfirmOpened,
    { open: openResendConfirm, close: closeResendConfirm },
  ] = useDisclosure(false);
  const formRef = useRef<HTMLFormElement>(null);
  const editRequestIdRef = useRef(0);

  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToToggle, setUserToToggle] = useState<User | null>(null);
  const [userToResend, setUserToResend] = useState<User | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<UserFilters>({});
  const [tempFilters, setTempFilters] = useState<UserFilters>({});
  const [page, setPage] = useState(1);
  const [sendingInviteUserId, setSendingInviteUserId] = useState<string | null>(
    null
  );
  const [loadingEditUserId, setLoadingEditUserId] = useState<string | null>(
    null
  );

  const currentUserId = useAuthStore((s) => s.profile?.id);

  const { data: users = [], isLoading, refetch } = useUsersAdmin();
  const { data: roles = [] } = useRolesAdmin();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const toggleUserActive = useToggleUserActive();
  const setDefaultQaReviewer = useSetDefaultQaReviewer();
  const clearDefaultQaReviewer = useClearDefaultQaReviewer();
  const sendInvitation = useSendInvitation();

  const filteredUsers = useMemo(() => {
    let result = users;

    if (filters.roleId) {
      result = result.filter((u) => u.role_id === filters.roleId);
    }

    if (filters.status === "active") {
      result = result.filter((u) => u.is_active);
    } else if (filters.status === "inactive") {
      result = result.filter((u) => !u.is_active);
    }

    if (filters.defaultQaReviewer === "yes") {
      result = result.filter((u) => u.isDefaultQaReviewer);
    } else if (filters.defaultQaReviewer === "no") {
      result = result.filter((u) => !u.isDefaultQaReviewer);
    }

    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(
        (u) =>
          u.full_name?.toLowerCase().includes(searchLower) ||
          u.username?.toLowerCase().includes(searchLower) ||
          u.role?.name?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [users, debouncedSearch, filters]);

  const hasActiveFilters =
    !!filters.roleId ||
    (filters.status && filters.status !== "all") ||
    (filters.defaultQaReviewer && filters.defaultQaReviewer !== "all");

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredUsers, page]);

  const roleOptions = roles
    .filter((r) => r.isActive)
    .map((r) => ({ value: r.id, label: r.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleAdd = useCallback(() => {
    editRequestIdRef.current += 1;
    setLoadingEditUserId(null);
    setEditingUser(null);
    openFormModal();
  }, [openFormModal]);

  const openEditModalForUser = useCallback(
    async (userId: string, requestId: number) => {
      const user = await usersService.getById(userId);
      if (editRequestIdRef.current !== requestId) return;
      setEditingUser(user);
      openFormModal();
    },
    [openFormModal]
  );

  const handleEdit = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      const userId = event.currentTarget.dataset.userId;
      if (!userId) return;

      const requestId = editRequestIdRef.current + 1;
      editRequestIdRef.current = requestId;
      setLoadingEditUserId(userId);
      try {
        await openEditModalForUser(userId, requestId);
      } catch {
        showUserLoadError();
      } finally {
        if (editRequestIdRef.current === requestId) {
          setLoadingEditUserId(null);
        }
      }
    },
    [openEditModalForUser]
  );

  const handleSubmit = useCallback(
    async (values: UserFormValues) => {
      const {
        is_default_qa_reviewer: isDefaultQa,
        is_remote: isRemote,
        no_shipping: noShipping,
        remote_address_street: remoteAddressStreet,
        remote_address_city: remoteAddressCity,
        remote_address_state: remoteAddressState,
        remote_address_zip: remoteAddressZip,
        ...rest
      } = values;

      const submissionValues = {
        ...rest,
        facility_id: rest.facility_id === "" ? null : rest.facility_id,
        credentials: values.credentials || undefined,
        pathologist_maximum_daily_cases:
          typeof values.pathologist_maximum_daily_cases === "string" &&
          values.pathologist_maximum_daily_cases === ""
            ? undefined
            : values.pathologist_maximum_daily_cases === 0 ||
              values.pathologist_maximum_daily_cases === "0"
            ? 0
            : Number(values.pathologist_maximum_daily_cases) || undefined,
      };

      try {
        if (editingUser) {
          await updateUser.mutateAsync({
            id: editingUser.id,
            ...submissionValues,
          });

          try {
            await usersService.updateRemoteShippingAddress(editingUser.id, {
              is_remote: isRemote,
              no_shipping: isRemote ? noShipping : false,
              address: {
                street: remoteAddressStreet || null,
                city: remoteAddressCity || null,
                state: remoteAddressState || null,
                zip: remoteAddressZip || null,
              },
            });
            await queryClient.invalidateQueries({
              queryKey: ["admin", "users"],
            });
          } catch {
            notifications.show({
              title: "Remote Address Save Failed",
              message:
                "User was updated but remote shipping address could not be saved.",
              color: "red",
            });
          }

          const wasDefault = editingUser.isDefaultQaReviewer ?? false;
          if (isDefaultQa && !wasDefault) {
            await setDefaultQaReviewer.mutateAsync(editingUser.id);
          } else if (!isDefaultQa && wasDefault) {
            await clearDefaultQaReviewer.mutateAsync(editingUser.id);
          }
        } else {
          const result = await createUser.mutateAsync(submissionValues);
          const newUserId = result?.id;

          if (newUserId) {
            if (isDefaultQa) {
              await setDefaultQaReviewer.mutateAsync(newUserId);
            }

            try {
              await usersService.updateRemoteShippingAddress(newUserId, {
                is_remote: isRemote,
                no_shipping: isRemote ? noShipping : false,
                address: {
                  street: remoteAddressStreet || null,
                  city: remoteAddressCity || null,
                  state: remoteAddressState || null,
                  zip: remoteAddressZip || null,
                },
              });
              await queryClient.invalidateQueries({
                queryKey: ["admin", "users"],
              });
            } catch {
              notifications.show({
                title: "Remote Address Save Failed",
                message:
                  "User was created but remote shipping address could not be saved.",
                color: "red",
              });
            }
          }

          if (result?.warning) {
            notifications.show({
              title: "User Created",
              message:
                "User created but invitation email failed to send. Use the Send Invitation button to retry.",
              color: "orange",
            });
          } else {
            notifications.show({
              title: "User Created",
              message: "User created and invitation sent.",
              color: "green",
            });
          }
        }
        closeFormModal();
      } catch {
        // Error notification is handled by the mutation's onError callback
      }
    },
    [
      clearDefaultQaReviewer,
      closeFormModal,
      createUser,
      editingUser,
      queryClient,
      setDefaultQaReviewer,
      updateUser,
    ]
  );

  const handleToggleActive = (user: User) => {
    setUserToToggle(user);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (userToToggle) {
      try {
        await toggleUserActive.mutateAsync({
          id: userToToggle.id,
          isActive: !userToToggle.is_active,
        });
        closeConfirmModal();
        setUserToToggle(null);
      } catch {
        // Error notification is handled by the mutation's onError callback
      }
    }
  };

  const handleSendInvitation = (user: User) => {
    if (user.invitationStatus === "activated") {
      setUserToResend(user);
      openResendConfirm();
    } else {
      setSendingInviteUserId(user.id);
      sendInvitation.mutate(user.id, {
        onSettled: () => setSendingInviteUserId(null),
      });
    }
  };

  const confirmResendInvitation = () => {
    if (userToResend) {
      setSendingInviteUserId(userToResend.id);
      sendInvitation.mutate(userToResend.id, {
        onSettled: () => setSendingInviteUserId(null),
      });
      closeResendConfirm();
      setUserToResend(null);
    }
  };

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    closeFilterModal();
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
  };

  const handleRemoveFilter = (key: keyof UserFilters) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
    setPage(1);
  };

  const getFilterDisplayName = (key: keyof UserFilters): string | null => {
    if (key === "roleId" && filters.roleId) {
      return roles.find((r) => r.id === filters.roleId)?.name || null;
    }
    if (key === "status" && filters.status && filters.status !== "all") {
      return filters.status === "active" ? "Active Only" : "Inactive Only";
    }
    if (
      key === "defaultQaReviewer" &&
      filters.defaultQaReviewer &&
      filters.defaultQaReviewer !== "all"
    ) {
      return filters.defaultQaReviewer === "yes"
        ? "Default QA: Yes"
        : "Default QA: No";
    }
    return null;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Users</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage system users and access
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by name, username, or role..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" align="center">
                    <Text size="sm" c="dimmed">
                      Filters:
                    </Text>
                    {filters.roleId && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("roleId")}
                      >
                        {getFilterDisplayName("roleId")}
                      </Pill>
                    )}
                    {filters.status && filters.status !== "all" && (
                      <Pill
                        withRemoveButton
                        onRemove={() => handleRemoveFilter("status")}
                      >
                        {getFilterDisplayName("status")}
                      </Pill>
                    )}
                    {filters.defaultQaReviewer &&
                      filters.defaultQaReviewer !== "all" && (
                        <Pill
                          withRemoveButton
                          onRemove={() =>
                            handleRemoveFilter("defaultQaReviewer")
                          }
                        >
                          {getFilterDisplayName("defaultQaReviewer")}
                        </Pill>
                      )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  Showing {filteredUsers.length} of {users.length} users
                </Text>
              </Stack>
            </Paper>
          </Group>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Users ({filteredUsers.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(page * ITEMS_PER_PAGE, filteredUsers.length)} of{" "}
                      {filteredUsers.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredUsers.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search || hasActiveFilters
                    ? "No users match the current filters"
                    : "No users found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Full Name</Table.Th>
                      <Table.Th>Email</Table.Th>
                      <Table.Th>Role</Table.Th>
                      <Table.Th>Group</Table.Th>
                      <Table.Th>Facility</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th style={{ width: 110, minWidth: 110 }}>
                        Actions
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedUsers.map((user) => (
                      <Table.Tr
                        key={user.id}
                        style={{ opacity: user.is_active ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {user.full_name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            ({user.username})
                          </Text>
                          <Group gap={4} mt={2}>
                            {user.isDefaultQaReviewer && (
                              <Badge color="violet" variant="light" size="xs">
                                Default QA Reviewer
                              </Badge>
                            )}
                            {user.is_remote && (
                              <>
                                {(() => {
                                  const hasStreet =
                                    !!user.remote_address_street;
                                  const hasCity = !!user.remote_address_city;
                                  const hasState = !!user.remote_address_state;
                                  const hasZip = !!user.remote_address_zip;
                                  const isComplete =
                                    hasStreet && hasCity && hasState && hasZip;
                                  return (
                                    <Badge
                                      color={isComplete ? "blue" : "orange"}
                                      variant="light"
                                      size="xs"
                                    >
                                      {isComplete
                                        ? "Remote: Address set"
                                        : "Remote: Address required"}
                                    </Badge>
                                  );
                                })()}
                              </>
                            )}
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{user.email || "\u2014"}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{user.role?.name || "\u2014"}</Text>
                          {user.credentials && (
                            <Text size="xs" c="dimmed">
                              {user.credentials}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {user.userGroups && user.userGroups.length > 0
                              ? user.userGroups
                                  .map((ug) => ug.group?.name ?? "")
                                  .filter(Boolean)
                                  .join(", ")
                              : "\u2014"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {user.facility?.name || "\u2014"}
                          </Text>
                          {user.pathologist_maximum_daily_cases != null && (
                            <Text size="xs" c="dimmed">
                              Max {user.pathologist_maximum_daily_cases}/day
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {(() => {
                            const { color, label } = getUserStatusBadge(user);
                            return (
                              <Badge color={color} variant="light" size="sm">
                                {label}
                              </Badge>
                            );
                          })()}
                        </Table.Td>
                        <Table.Td style={{ whiteSpace: "nowrap" }}>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                aria-label={`Edit ${user.full_name}`}
                                data-user-id={user.id}
                                disabled={loadingEditUserId !== null}
                                loading={loadingEditUserId === user.id}
                                onClick={handleEdit}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            {user.is_active &&
                              user.id !== currentUserId &&
                              (() => {
                                const inv =
                                  user.invitationStatus || "not_invited";
                                const isLoading =
                                  sendingInviteUserId === user.id;
                                if (inv === "activated") {
                                  return (
                                    <Tooltip label="Resend Invitation (already activated)">
                                      <ActionIcon
                                        variant="subtle"
                                        color="green"
                                        onClick={() =>
                                          handleSendInvitation(user)
                                        }
                                        loading={isLoading}
                                      >
                                        <IconRotate size={18} />
                                      </ActionIcon>
                                    </Tooltip>
                                  );
                                }
                                if (inv === "invited") {
                                  return (
                                    <Tooltip label="Resend Invitation">
                                      <ActionIcon
                                        variant="subtle"
                                        color="orange"
                                        onClick={() =>
                                          handleSendInvitation(user)
                                        }
                                        loading={isLoading}
                                      >
                                        <IconRotate size={18} />
                                      </ActionIcon>
                                    </Tooltip>
                                  );
                                }
                                return (
                                  <Tooltip label="Send Invitation">
                                    <ActionIcon
                                      variant="subtle"
                                      color="teal"
                                      onClick={() => handleSendInvitation(user)}
                                      loading={isLoading}
                                    >
                                      <IconSend size={18} />
                                    </ActionIcon>
                                  </Tooltip>
                                );
                              })()}
                            {user.id !== currentUserId && (
                              <Tooltip
                                label={
                                  user.is_active ? "Deactivate" : "Activate"
                                }
                              >
                                <ActionIcon
                                  variant="subtle"
                                  color={user.is_active ? "red" : "green"}
                                  onClick={() => handleToggleActive(user)}
                                >
                                  {user.is_active ? (
                                    <IconX size={18} />
                                  ) : (
                                    <IconCheck size={18} />
                                  )}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Add/Edit User Modal — fixed header/footer, scrollable body */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editingUser ? "Edit User" : "Add New User"}
          closeOnClickOutside={false}
          size="xl"
          styles={{ body: { padding: 0 } }}
        >
          <ScrollArea.Autosize
            mah="calc(100vh - 300px)"
            offsetScrollbars
            px="md"
            pt="md"
          >
            <UserForm
              key={editingUser?.id ?? "new-user"}
              formRef={formRef}
              initialValues={editingUser || undefined}
              onSubmit={handleSubmit}
              isLoading={createUser.isPending || updateUser.isPending}
            />
          </ScrollArea.Autosize>
          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                loading={createUser.isPending || updateUser.isPending}
                onClick={() => {
                  formRef.current?.requestSubmit();
                }}
              >
                {editingUser ? "Update" : "Create User"}
              </Button>
            </Group>
          </Box>
        </Modal>

        {/* Filter Modal — design standard */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Users"
          closeOnClickOutside={false}
          size="sm"
          styles={{ body: { padding: 0 } }}
        >
          <Stack gap="md" px="md" pt="md">
            <Select
              label="Role"
              placeholder="All roles"
              data={roleOptions}
              value={tempFilters.roleId || null}
              onChange={(value) =>
                setTempFilters((prev) => ({ ...prev, roleId: value }))
              }
              clearable
              searchable
            />
            <Select
              label="Status"
              placeholder="All statuses"
              data={[
                { value: "all", label: "All" },
                { value: "active", label: "Active Only" },
                { value: "inactive", label: "Inactive Only" },
              ]}
              value={tempFilters.status || "all"}
              onChange={(value) =>
                setTempFilters((prev) => ({
                  ...prev,
                  status: value as UserFilters["status"],
                }))
              }
            />
            <Select
              label="Default QA Reviewer"
              placeholder="All users"
              data={[
                { value: "all", label: "All" },
                { value: "yes", label: "Default QA Reviewer Only" },
                { value: "no", label: "Not Default QA Reviewer" },
              ]}
              value={tempFilters.defaultQaReviewer || "all"}
              onChange={(value) =>
                setTempFilters((prev) => ({
                  ...prev,
                  defaultQaReviewer: value as UserFilters["defaultQaReviewer"],
                }))
              }
            />
          </Stack>
          <Box
            px="md"
            py="sm"
            mt="md"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={closeFilterModal}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Box>
        </Modal>

        {/* Activate/Deactivate Confirmation — design standard */}
        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={userToToggle?.is_active ? "Deactivate User" : "Activate User"}
          size="sm"
          centered
          styles={{ body: { padding: 0 } }}
        >
          <Stack gap="md" px="md" pt="md">
            <Text>
              Are you sure you want to{" "}
              {userToToggle?.is_active ? "deactivate" : "activate"} the user{" "}
              <strong>{userToToggle?.full_name}</strong>?
            </Text>
            {userToToggle?.is_active && (
              <Text size="sm" c="dimmed">
                Deactivated users will no longer be able to log in.
              </Text>
            )}
          </Stack>
          <Box
            px="md"
            py="sm"
            mt="md"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={userToToggle?.is_active ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleUserActive.isPending}
              >
                {userToToggle?.is_active ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Box>
        </Modal>

        {/* Resend Invitation Confirmation */}
        <Modal
          opened={resendConfirmOpened}
          onClose={closeResendConfirm}
          title="Resend Invitation"
          size="sm"
          centered
          styles={{ body: { padding: 0 } }}
        >
          <Stack gap="md" px="md" pt="md">
            <Text>
              This user has already activated their account. Resending the
              invitation will require them to set a new password.
            </Text>
            <Text size="sm" c="dimmed">
              User: <strong>{userToResend?.full_name}</strong>
            </Text>
          </Stack>
          <Box
            px="md"
            py="sm"
            mt="md"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={closeResendConfirm}>
                Cancel
              </Button>
              <Button
                color="teal"
                onClick={confirmResendInvitation}
                loading={sendInvitation.isPending}
              >
                Resend Invitation
              </Button>
            </Group>
          </Box>
        </Modal>
      </Box>
    </Container>
  );
}
